import { ExamSession } from "../../redux/apiSlice/examinerSlice";

/* Why this exists: verifying a candidate whose lab has not finished provisioning is
   refused by the backend, and until now the only way for an examiner to find that
   out was to click Verify and read the error. This module gives the examiner screen
   a real readiness signal so Verify is only offered when it will actually work. */

export type LabState =
  "not_required" | "ready" | "provisioning" | "pending" | "unknown";

/* Mirrors the payload of GET examinstances/{id}/lab/status, which is also embedded
   as `lab` in a 409 from the verify endpoint. */
export type LabReadiness = {
  lab_required: boolean;
  ready: boolean;
  state: LabState;
  lab_status?: string;
  provisioning_state?: string;
  message: string;
  checked_at?: number;
};

export const LAB_STATE_LABEL: Record<LabState, string> = {
  not_required: "No lab",
  ready: "Ready",
  provisioning: "Provisioning",
  pending: "Not started",
  unknown: "Unknown",
};

/* antd Tag presets. `pending` and `unknown` are deliberately different colours:
   waiting for the provisioner is normal, a lab service we cannot reach is not. */
export const LAB_STATE_COLOR: Record<LabState, string | undefined> = {
  not_required: undefined,
  ready: "success",
  provisioning: "processing",
  pending: "warning",
  unknown: "error",
};

const LAB_STATE_MESSAGE: Record<LabState, string> = {
  not_required:
    "This exam has no lab — nothing needs to be provisioned before verifying.",
  ready: "Lab infrastructure is ready — this session can be verified.",
  provisioning:
    "Lab infrastructure is still coming up. This usually takes a few minutes.",
  pending:
    "Lab infrastructure has not been provisioned yet. It is created automatically in the hour before the slot.",
  unknown: "Lab status could not be checked right now.",
};

/* The readiness the list payload alone already implies, so the column has something
   truthful to show on first paint instead of a spinner. `ready` matches what the
   verify endpoint gates on — the stored lab_status — never a guess. */
export function storedLabReadiness(session: ExamSession): LabReadiness {
  if (!session.exam?.lab_available) {
    return {
      lab_required: false,
      ready: true,
      state: "not_required",
      message: LAB_STATE_MESSAGE.not_required,
    };
  }

  const state: LabState =
    session.lab_status === "available"
      ? "ready"
      : session.lab_status === "provisioning"
        ? "provisioning"
        : "pending";

  return {
    lab_required: true,
    ready: state === "ready",
    state,
    lab_status: session.lab_status,
    message: LAB_STATE_MESSAGE[state],
  };
}

/* Which of the two readiness signals to believe.
 *
 * Both are reads of the same lab_status column — the list payload as of the last
 * table refresh, and the live check as of its own last poll — and neither the cron
 * nor the readiness endpoint ever demotes an available lab. So "ready" from either
 * one is true, and a live result cached before the lab came up must not mask a
 * fresher list that already says available. Everything else is fresher live. */
export function mergeLabReadiness(
  stored: LabReadiness,
  live: LabReadiness | undefined,
): LabReadiness {
  if (!live) return stored;
  if (stored.ready && !live.ready) return stored;
  return live;
}

/* StartExamLabs provisions from an hour before the slot, so before that the honest
   answer is "not started yet" and there is nothing to watch. */
const PROVISIONING_WINDOW_MS = 60 * 60 * 1000;

/* Auto-refresh is limited to sessions whose lab is actually being waited on: a paper
   with no lab has nothing to poll, a ready lab cannot become un-ready from here, and
   a slot days away has no infrastructure yet by design — polling those would be a
   steady stream of AWS lookups for an answer we already know. */
export function needsLabPolling(
  session: ExamSession,
  readiness: LabReadiness,
): boolean {
  if (!session.exam?.lab_available || readiness.ready) return false;
  if (session.status === "started") return true;

  return (
    session.status === "scheduled" &&
    session.time * 1000 - Date.now() < PROVISIONING_WINDOW_MS
  );
}
